/**
 * t258u: one task comment on the desktop (task panel and feedback). Your own comment has a small Edit / Delete pair
 * (inline edit, Enter saves only a real change, Esc cancels; delete asks first); edited comments say "(edited)".
 * Deleted comments aren't shown: the list is flat, so nothing hangs off them.
 */
import type { ReactNode } from 'react';
import type { TaskComment } from '@tradeoutlet/shared';
import { Avatar, Button, Textarea } from '@/components/ui';
import { PersonButton } from '@/features/people/PersonDrawer';
import { MessageBody } from '@/features/chat/MessageBody';
import { messageTimestamp } from '@/lib/format';
import type { useTaskCommentActions } from './taskCommentActions';
import { enterToSend } from '@/lib/enterToSend';

export function TaskCommentItem({ c, name, mine, act, body, id }: {
  c: TaskComment; name: string | undefined; mine: boolean; act: ReturnType<typeof useTaskCommentActions>;
  /** How to show the text (e.g. with translation); defaults to the plain body. */ body?: ReactNode; id?: string;
}) {
  const editing = act.editing?.id === c.id ? act.editing : null;
  return (
    <div id={id} className="group flex gap-2.5 rounded-lg transition-colors" data-testid="task-comment" data-mine={mine || undefined}>
      <Avatar size={28} name={name} id={c.author_id} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <PersonButton userId={c.author_id} name={name} className="rounded text-sm font-semibold hover:underline">{name ?? 'Someone'}</PersonButton>
          <span className="text-xs text-slate-500">{messageTimestamp(c.created_at)}</span>
          {mine && !editing && (
            <span className="ml-auto flex gap-2 text-xs text-slate-500 opacity-70 group-hover:opacity-100 group-focus-within:opacity-100">
              <button type="button" className="hover:text-slate-900 hover:underline dark:hover:text-white" onClick={() => act.start(c)} data-testid="task-comment-edit">Edit</button>
              <button type="button" className="hover:text-rose-700 hover:underline" onClick={() => void act.remove(c)} data-testid="task-comment-delete">Delete</button>
            </span>
          )}
        </div>
        {editing ? (
          <div className="mt-1 space-y-2">
            <Textarea autoFocus rows={2} value={editing.text} aria-label="Edit comment" onChange={e => act.setText(e.target.value)} data-testid="task-comment-edit-box"
              onKeyDown={enterToSend({ value: editing.text, onSend: () => void act.save(c), onCancel: act.cancel })} />
            <div className="flex gap-2"><Button size="sm" loading={act.busy} disabled={!editing.text.trim()} onClick={() => void act.save(c)} data-testid="task-comment-save">Save</Button><Button size="sm" variant="ghost" onClick={act.cancel}>Cancel</Button></div>
          </div>
        ) : (
          <div className="text-sm">{body ?? <MessageBody body={c.body} />}{c.edited_at && <span className="text-[11px] text-slate-500" data-testid="comment-edited"> (edited)</span>}</div>
        )}
      </div>
    </div>
  );
}
